import { PaginationDto, IPaginationEntity } from '@maia/pagination';

export abstract class BaseController {
  protected async getEntities<T, D>(
    getFunction: {
      findMany: (
        path: string,
        search: Record<string, unknown>,
      ) => Promise<{ response: Array<T>; pagination: IPaginationEntity }>;
    },
    { path, search }: { path: string; search: Record<string, unknown> },
    Dto: new (entity: T) => D,
  ): Promise<{ response: D[]; pagination: PaginationDto }> {
    const { sort, ...rest } = search;
    const parsedSearch = {
      ...rest,
      ...(sort ? transformSortString(sort as string) : {}),
    };

    const { response, pagination } = await getFunction.findMany(
      path,
      parsedSearch,
    );

    return {
      response: response.map((entity) => new Dto(entity)),
      pagination: new PaginationDto(pagination),
    };
  }

  protected async getEntity<T, D>(
    getFunction: { find: (path: string) => Promise<T> },
    path: string,
    Dto: new (entity: T) => D,
  ): Promise<D> {
    const entity = await getFunction.find(path);
    return new Dto(entity);
  }

  protected async post<T, D>(
    func: (path: string, body: Record<string, unknown>) => Promise<T>,
    path: string,
    body: Record<string, unknown>,
    Dto: new (entity: T) => D,
  ): Promise<D> {
    const entity = await func(path, body);
    return new Dto(entity);
  }

  protected async getSomeEntities<T, D>(
    func: (path: string) => Promise<Array<T>>,
    { path }: { path: string },
    Dto: new (entity: T) => D,
  ) {
    const entities = await func(path);
    return entities.map((entity) => new Dto(entity));
  }
}

function transformSortString(sortString: string) {
  const [key, value] = sortString.replace('sort=', '').split(',');
  return { [key]: value };
}
